import { useEffect, useRef } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useCurrentTenant } from "./use-current-tenant"
import { queryKeys } from "./query-keys"

export interface UseTenantChangeSyncOptions {
  /** Forces the SignalR connection to tear down and rejoin the correct tenant group. */
  reconnect: () => Promise<void>
  /** Called once a genuine tenant change is detected, for page-local cleanup. */
  onTenantChanged?: (newTenantId: number, previousTenantId: number) => void
}

/**
 * Detects a mid-session Director tenant/facility switch (FAB-16062/FAB-16186)
 * and resyncs the SignalR connection to the new tenant's group. Projects,
 * report lists, and reports are cached per tenant id (see query-keys.ts), so
 * switching back to a previously-visited tenant restores its cached data
 * instantly rather than needing an explicit refetch here.
 * Never fires on the initial fetch — only on a genuine change thereafter.
 */
export function useTenantChangeSync({
  reconnect,
  onTenantChanged,
}: UseTenantChangeSyncOptions): void {
  const queryClient = useQueryClient()
  const { data: tenant } = useCurrentTenant()
  const previousTenantIdRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    // Bypasses React Query's internal focus-manager wiring so a tab refocus
    // triggers this refetch immediately rather than waiting on its own timing.
    const refetchTenant = () => {
      void queryClient.refetchQueries({ queryKey: queryKeys.tenant.all })
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") refetchTenant()
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)
    // Belt-and-suspenders alongside visibilitychange/refetchOnWindowFocus for
    // browsers/cases where one event fires without the other.
    window.addEventListener("focus", refetchTenant)

    // Neither event above reliably fires when switching between two separate
    // top-level browser windows (as opposed to tabs within the same window) —
    // both windows can stay "visible" and never blur/focus in that case. Poll
    // document.hasFocus() directly as a cheap, event-independent fallback.
    let wasFocused = document.hasFocus()
    const focusPollId = window.setInterval(() => {
      const isFocused = document.hasFocus()
      if (isFocused && !wasFocused) refetchTenant()
      wasFocused = isFocused
    }, 2_000)

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
      window.removeEventListener("focus", refetchTenant)
      window.clearInterval(focusPollId)
    }
  }, [queryClient])

  useEffect(() => {
    if (tenant === undefined) return

    const previousTenantId = previousTenantIdRef.current
    previousTenantIdRef.current = tenant.id

    if (previousTenantId === undefined || previousTenantId === tenant.id) {
      return
    }

    void reconnect()
    onTenantChanged?.(tenant.id, previousTenantId)
  }, [tenant, reconnect, onTenantChanged])
}
